import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React from 'react';

/** Props forwarded to the drag handle element inside each sortable item */
interface DragHandleProps {
  /** Spread these onto the drag handle element */
  listeners: ReturnType<typeof useSortable>['listeners'];
  /** Spread these onto the drag handle element */
  attributes: ReturnType<typeof useSortable>['attributes'];
  /** Apply this style to the drag handle (includes touch-action: none) */
  style: React.CSSProperties;
}

interface SortableItemProps {
  id: string;
  children: (dragHandleProps: DragHandleProps) => React.ReactNode;
}

/** Wraps a single sortable item — drag is activated via a dedicated handle */
const SortableItem: React.FC<SortableItemProps> = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  /** Style for the outer wrapper — allows native scrolling */
  const wrapperStyle: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative',
  };

  /** Props to spread on the drag handle inside the card */
  const dragHandleProps: DragHandleProps = {
    listeners,
    attributes,
    style: { touchAction: 'none', cursor: 'grab' },
  };

  return (
    <div ref={setNodeRef} style={wrapperStyle}>
      {children(dragHandleProps)}
    </div>
  );
};

/** Activation constraint: hold for 0 second with ≤5px tolerance */
const LONG_PRESS_CONSTRAINT = { delay: 0, tolerance: 5 } as const;

interface SortableListProps<T> {
  /** Items to render in the sortable list */
  items: T[];
  /** Called when items are reordered. Receives the new ordered array. */
  onReorder: (reorderedItems: T[]) => void;
  /** Render function for each item — receives drag handle props to attach to a handle element */
  renderItem: (
    item: T,
    index: number,
    dragHandleProps: DragHandleProps,
  ) => React.ReactNode;
  /** Extract a unique ID from each item */
  getId: (item: T) => string;
  /** Optional class name for the list container */
  className?: string;
}

/**
 * Reusable drag-and-drop sortable list component.
 * Wraps children with @dnd-kit DnD context and sortable items.
 * Drag is activated via a dedicated handle (long-press 0s) to avoid
 * blocking native touch scrolling on mobile devices.
 */
function SortableList<T>({
  items,
  onReorder,
  renderItem,
  getId,
  className = '',
}: SortableListProps<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: LONG_PRESS_CONSTRAINT,
    }),
    useSensor(TouchSensor, {
      activationConstraint: LONG_PRESS_CONSTRAINT,
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex(item => getId(item) === active.id);
      const newIndex = items.findIndex(item => getId(item) === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const reordered = [...items];
        const [movedItem] = reordered.splice(oldIndex, 1);
        reordered.splice(newIndex, 0, movedItem);
        onReorder(reordered);
      }
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={items.map(item => getId(item))}
        strategy={verticalListSortingStrategy}
      >
        <div className={className}>
          {items.map((item, index) => (
            <SortableItem key={getId(item)} id={getId(item)}>
              {dragHandleProps => renderItem(item, index, dragHandleProps)}
            </SortableItem>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

export default SortableList;
export { SortableItem };
export type { DragHandleProps, SortableListProps };
